export const EMBED_HEIGHT_MESSAGE_TYPE = 'fleetwire:embed-height';
export const EMBED_BOOKING_COMPLETE_MESSAGE_TYPE = 'fleetwire:booking-complete';

export interface EmbedBookingCompletePayload {
    b_id: string;
    listing_id: string | null;
    listing_name: string | null;
    /** Decimal amount (e.g. 197.5), not cents — what ad platforms expect as `value`. */
    total: number | null;
    currency: string | null;
    trip_start: string | null;
    trip_end: string | null;
}

/**
 * When the app runs inside an iframe on someone else's site, report the
 * document height to the parent page so its embed snippet (see
 * utils/embedSnippets.ts iframeEmbedSnippet) can size the iframe to fit —
 * otherwise embedders are stuck guessing a fixed min-height.
 *
 * Top-level pages are a no-op: no listeners, no messages. The height is
 * broadcast with targetOrigin '*' because it is not sensitive and the
 * embedding site's origin is unknowable here; the host snippet only accepts
 * messages whose source is its own Fleetwire iframe.
 */
export function initEmbedHeightReporter(win: Window = window): void {
    if (win.self === win.top) {
        return;
    }

    const raf = typeof win.requestAnimationFrame === 'function'
        ? win.requestAnimationFrame.bind(win)
        : (callback: () => void) => win.setTimeout(callback, 50);

    let reportQueued = false;

    const reportHeight = () => {
        if (reportQueued) {
            return;
        }
        reportQueued = true;

        raf(() => {
            reportQueued = false;

            const height = Math.max(
                win.document.documentElement?.scrollHeight ?? 0,
                win.document.body?.scrollHeight ?? 0,
            );

            if (height > 0) {
                win.parent.postMessage({type: EMBED_HEIGHT_MESSAGE_TYPE, height}, '*');
            }
        });
    };

    // ResizeObserver catches content growth (checkout steps expanding, images
    // loading); the load/resize listeners cover environments without it.
    if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(reportHeight);
        observer.observe(win.document.documentElement);
        if (win.document.body) {
            observer.observe(win.document.body);
        }
    }

    win.addEventListener('load', reportHeight);
    win.addEventListener('resize', reportHeight);

    reportHeight();
}

/**
 * Tell the embedding page a booking was just confirmed so it can fire its own
 * analytics/ads conversions (the dashboard's Iframe tab documents the
 * listener). Mirrors trackPurchase's sessionStorage dedupe so a refresh of the
 * confirmation page doesn't double-fire the embedder's pixel. Top-level pages
 * are a no-op; the payload deliberately carries no renter details.
 */
export function postEmbedBookingComplete(payload: EmbedBookingCompletePayload, win: Window = window): void {
    if (win.self === win.top || !payload.b_id) {
        return;
    }

    const dedupeKey = `fw-embed-booking-complete-${payload.b_id}`;

    try {
        if (win.sessionStorage.getItem(dedupeKey)) {
            return;
        }
        win.sessionStorage.setItem(dedupeKey, '1');
    } catch {
        // sessionStorage unavailable (privacy mode) — fire anyway; ad platforms
        // dedupe on transaction id themselves.
    }

    win.parent.postMessage({type: EMBED_BOOKING_COMPLETE_MESSAGE_TYPE, booking: payload}, '*');
}
